/**
 * Campaign Milestones Page - Track project progress
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, CheckCircle, Clock, AlertCircle, Target } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CampaignMilestones = () => {
  const { slug } = useParams();

  const { data: campaign } = useQuery({
    queryKey: ['campaign', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${slug}`);
      return res.json();
    }
  });

  const { data: milestones = [], isLoading } = useQuery({
    queryKey: ['campaign-milestones', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${slug}/milestones`);
      return res.json();
    }
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-5 w-5 text-emerald-600" />;
      case 'in_progress': return <Clock className="h-5 w-5 text-amber-500" />;
      default: return <AlertCircle className="h-5 w-5 text-slate-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': return <Badge className="bg-emerald-100 text-emerald-800">Completed</Badge>;
      case 'in_progress': return <Badge className="bg-amber-100 text-amber-800">In Progress</Badge>;
      default: return <Badge variant="outline">Planned</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to={`/campaigns/${slug}`}
            className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Campaign
          </Link>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Target className="h-8 w-8 text-emerald-600" />
            Project Milestones
          </h1>
          {campaign && (
            <p className="text-slate-500 mt-1">{campaign.name}</p>
          )}
        </div>

        {/* Overall Progress */}
        {campaign && (
          <Card className="mb-8">
            <CardContent className="py-6">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium">Overall Project Progress</span>
                <span className="text-2xl font-bold text-emerald-600">{campaign.overall_progress || 0}%</span>
              </div>
              <Progress value={campaign.overall_progress || 0} className="h-4" />
            </CardContent>
          </Card>
        )}

        {/* Milestones List */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-32 w-full" />)}
          </div>
        ) : milestones.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <Target className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No milestones added yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {milestones.map((milestone, index) => (
              <Card key={milestone.id} className="relative overflow-hidden">
                {milestone.status === 'completed' && (
                  <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500" />
                )}
                {milestone.status === 'in_progress' && (
                  <div className="absolute top-0 left-0 w-1 h-full bg-amber-500" />
                )}
                <CardContent className="py-5">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 pt-1">
                      {getStatusIcon(milestone.status)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-sm text-slate-500 font-medium">Phase {milestone.phase || index + 1}</span>
                          <h3 className="text-lg font-semibold">{milestone.title}</h3>
                        </div>
                        {getStatusBadge(milestone.status)}
                      </div>
                      {milestone.description && (
                        <p className="text-slate-600 text-sm mb-3">{milestone.description}</p>
                      )}
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <Progress value={milestone.progress || 0} className="h-2" />
                        </div>
                        <span className="text-sm font-medium text-slate-700">{milestone.progress || 0}%</span>
                      </div>
                      {(milestone.start_date || milestone.end_date) && (
                        <div className="mt-2 text-xs text-slate-500">
                          {milestone.start_date && <span>Start: {milestone.start_date}</span>}
                          {milestone.start_date && milestone.end_date && <span className="mx-2">•</span>}
                          {milestone.end_date && <span>Target: {milestone.end_date}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignMilestones;
